import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Member, useScore } from '../../../context/ScoreContext';
import MemberViewModal from './MemberViewModal';

import { createPortal } from 'react-dom';

// ── Animated counter ──────────────────────────────────────────────────────────
function useAnimatedCount(target: number) {
  const [display, setDisplay] = useState(target);
  const [popKey, setPopKey] = useState(0);
  const prev = useRef(target);
  useEffect(() => {
    if (target === prev.current) return;
    const start = prev.current;
    const diff = target - start;
    prev.current = target;
    if (diff > 0) setPopKey(k => k + 1);
    const duration = 600;
    const startTime = performance.now();
    const animate = (now: number) => {
      const p = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(start + diff * eased));
      if (p < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [target]);
  return { display, popKey };
}

// ── Typewriter hook ───────────────────────────────────────────────────────────
function useTypewriter(text: string, speed = 48) {
  const [displayed, setDisplayed] = useState('');
  useEffect(() => {
    if (!text) { setDisplayed(''); return; }
    setDisplayed('');
    let i = 0;
    const timer = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) clearInterval(timer);
    }, speed);
    return () => clearInterval(timer);
  }, [text, speed]);
  return displayed;
}

// ── Rank badge ────────────────────────────────────────────────────────────────
function RankBadge({ rank, accentColor }: { rank: number; accentColor: string }) {
  if (rank === 1) return (
    <div className="w-6 h-6 flex items-center justify-center rounded-full shrink-0"
      style={{ background: 'linear-gradient(135deg, #ffd700, #d4a843)', boxShadow: '0 0 12px rgba(212,168,67,0.8), 0 0 24px rgba(212,168,67,0.4)' }}
    >
      <i className="ri-crown-fill text-[9px] text-[#0d0d0d]" />
    </div>
  );
  if (rank === 2) return (
    <div className="w-6 h-6 flex items-center justify-center rounded-full shrink-0"
      style={{ background: 'linear-gradient(135deg, #c0c0c0, #a0a0a0)', boxShadow: '0 0 8px rgba(200,200,200,0.5)' }}
    >
      <span className="text-[9px] font-black text-[#0d0d0d]">2</span>
    </div>
  );
  if (rank === 3) return (
    <div className="w-6 h-6 flex items-center justify-center rounded-full shrink-0"
      style={{ background: 'linear-gradient(135deg, #cd7f32, #a05a20)', boxShadow: '0 0 8px rgba(180,100,30,0.4)' }}
    >
      <span className="text-[9px] font-black text-[#fff]">3</span>
    </div>
  );
  return (
    <div className="w-6 h-6 flex items-center justify-center rounded-full shrink-0"
      style={{ background: `${accentColor}10`, border: `1px solid ${accentColor}20` }}
    >
      <span className="text-[9px] font-bold" style={{ color: `${accentColor}55` }}>{rank}</span>
    </div>
  );
}

// ── MemberCard ────────────────────────────────────────────────────────────────
function MemberCard({
  member, isMe, rank, score, onView,
}: {
  member: Member; isMe: boolean; rank: number; score: number; onView: () => void;
}) {
  const isBlack = member.team === 'black';
  const accentColor = isBlack ? '#d4a843' : '#e0e0e0';
  const isTop3 = rank <= 3;

  return (
    <div className="relative pt-2">
      {isMe && (
        <span className="absolute top-0 left-3 z-10 text-[9px] font-black tracking-widest uppercase px-2 py-0.5 rounded-full leading-none"
          style={{ background: accentColor, color: '#0d0d0d' }}
        >
          你
        </span>
      )}
      <div
        className={`relative rounded-xl border transition-all duration-300 cursor-pointer group active:scale-[0.98] overflow-hidden ${isMe ? 'ring-1' : ''}`}
        style={{
          borderColor: isTop3 && !isMe ? `${accentColor}45` : isMe ? accentColor : '#1e1e1e',
          background: isMe
            ? `linear-gradient(135deg, ${accentColor}18, ${accentColor}06)`
            : isTop3
              ? `linear-gradient(135deg, ${accentColor}10, #0e0e0e)`
              : 'linear-gradient(135deg, #111, #0e0e0e)',
          boxShadow: isMe
            ? `0 0 20px ${accentColor}25, 0 0 40px ${accentColor}10`
            : isTop3
              ? `0 0 14px ${accentColor}12`
              : 'none',
        }}
        onClick={onView}
      >
        {/* Hover shimmer */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
          style={{ background: `linear-gradient(105deg, transparent 40%, ${accentColor}12 50%, transparent 60%)`, animation: 'cardShimmer 1.5s ease infinite' }}
        />
        {/* Left accent bar */}
        <div className="absolute left-0 top-0 bottom-0 w-0.5 rounded-l-xl"
          style={{
            background: rank === 1
              ? 'linear-gradient(180deg, #ffd700, #d4a843)'
              : rank === 2 ? 'linear-gradient(180deg, #c0c0c0, #888)'
                : rank === 3 ? 'linear-gradient(180deg, #cd7f32, #a05a20)'
                  : isMe ? accentColor : `${accentColor}22`,
            boxShadow: rank <= 3 ? `0 0 8px ${rank === 1 ? '#d4a843' : rank === 2 ? '#c0c0c0' : '#cd7f32'}` : 'none',
          }}
        />

        <div className="flex items-center gap-2.5 px-3 py-2.5 pl-4">
          <RankBadge rank={rank} accentColor={accentColor} />

          {/* avatar */}
          <div className="relative shrink-0">
            {member.avatarUrl ? (
              <img src={member.avatarUrl} alt={member.name}
                className="w-9 h-9 rounded-full object-cover object-top"
                style={{ border: `2px solid ${accentColor}55`, boxShadow: isMe ? `0 0 14px ${accentColor}55` : `0 0 8px ${accentColor}25` }}
              />
            ) : (
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-black"
                style={{
                  background: `radial-gradient(circle at 35% 35%, ${accentColor}30, ${accentColor}08)`,
                  color: accentColor,
                  border: `2px solid ${accentColor}50`,
                  boxShadow: isMe ? `0 0 14px ${accentColor}45` : `0 0 6px ${accentColor}20`,
                }}
              >
                {member.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-[#0d0d0d]"
              style={{ background: '#7ec8a0', boxShadow: '0 0 4px rgba(126,200,160,0.7)' }}
            />
          </div>

          {/* info */}
          <div className="flex-1 min-w-0">
            <span className="text-sm font-bold truncate block leading-tight transition-colors group-hover:text-white"
              style={{ color: isMe ? 'white' : '#ccc' }}
            >
              {member.name}
            </span>
            {member.goal ? (
              <p className="text-[10px] leading-snug line-clamp-1 flex items-start gap-1 mt-0.5" style={{ color: '#555' }}>
                <i className="ri-flag-2-line shrink-0 mt-0.5" style={{ color: `${accentColor}60` }} />
                <span className="truncate">{member.goal}</span>
              </p>
            ) : (
              <p className="text-[10px] italic mt-0.5" style={{ color: '#333' }}>未設定目標</p>
            )}
          </div>

          {/* Score + arrow */}
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span className="text-sm font-black font-orbitron tabular-nums leading-none"
              style={{ color: score > 0 ? accentColor : '#333', textShadow: score > 0 ? `0 0 10px ${accentColor}80` : 'none' }}
            >
              {score}
            </span>
            <i className="ri-arrow-right-s-line text-xs transition-all group-hover:translate-x-0.5 group-hover:opacity-100 opacity-0"
              style={{ color: accentColor }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── TeamIntroCard ─────────────────────────────────────────────────────────────
function TeamIntroCard({ teamName, canEdit }: { teamName: 'black' | 'white'; canEdit: boolean }) {
  const { teamMottos, updateTeamMotto } = useScore();
  const isBlack = teamName === 'black';
  const accentColor = isBlack ? '#d4a843' : '#e0e0e0';
  const motto = teamMottos[teamName];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(motto);
  const typedMotto = useTypewriter(editing ? '' : motto, 50);

  useEffect(() => {
    if (!editing) setDraft(motto);
  }, [motto, editing]);

  const handleSave = () => { updateTeamMotto(teamName, draft.trim()); setEditing(false); };
  const handleCancel = () => { setDraft(motto); setEditing(false); };

  const imageUrl = isBlack
    ? 'https://public.readdy.ai/ai/img_res/ffe55c38b748c28085d2ffc9589bd2c9.jpg'
    : 'https://readdy.ai/api/search-image?query=white%20knight%20warrior%20bright%20light%20silhouette%20glowing%20cinematic%20misty%20atmosphere%20dramatic%20rays%20white%20light%20epic%20heroic%20battlefield%20background%20fantasy%20art%20premium&width=640&height=260&seq=whiteteam05&orientation=landscape';

  return (
    <div
      className="rounded-xl border overflow-hidden mb-4 group relative"
      style={{ borderColor: `${accentColor}30`, background: `${accentColor}05`, boxShadow: `0 0 30px ${accentColor}06` }}
    >
      {/* Corner accents */}
      <div className="absolute top-0 left-0 w-5 h-5 border-l-2 border-t-2 z-10 pointer-events-none" style={{ borderColor: `${accentColor}70` }} />
      <div className="absolute top-0 right-0 w-5 h-5 border-r-2 border-t-2 z-10 pointer-events-none" style={{ borderColor: `${accentColor}70` }} />
      <div className="absolute bottom-0 left-0 w-5 h-5 border-l-2 border-b-2 z-10 pointer-events-none" style={{ borderColor: `${accentColor}40` }} />
      <div className="absolute bottom-0 right-0 w-5 h-5 border-r-2 border-b-2 z-10 pointer-events-none" style={{ borderColor: `${accentColor}40` }} />

      {/* Banner */}
      <div className="relative h-32 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${accentColor}60, transparent)` }} />
        <img src={imageUrl} alt={isBlack ? 'Black Team' : 'White Team'}
          className="w-full h-full object-cover object-top transition-all duration-700 scale-105 group-hover:scale-100"
          style={{ opacity: isBlack ? 0.5 : 0.4 }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/25 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d]/40 via-transparent to-transparent" />
        {/* Sweep on hover */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
          style={{ background: `linear-gradient(120deg, transparent 25%, ${accentColor}18 50%, transparent 75%)` }}
        />
        <div className="absolute bottom-3 left-4">
          <span className="text-[10px] tracking-[0.45em] uppercase font-bold block font-cinzel"
            style={{ color: `${accentColor}90`, textShadow: `0 0 12px ${accentColor}` }}
          >
            {isBlack ? 'BLACK TEAM' : 'WHITE TEAM'}
          </span>
        </div>
      </div>

      {/* Intro text */}
      <div className="px-4 pt-3 pb-1">
        <p className="text-[#666] text-xs leading-relaxed">
          {isBlack
            ? '黑隊以智謀與沉穩著稱，在每一場對決中以策略取勝，讓對手措手不及。'
            : '白隊以熱血與衝勁聞名，以無畏的氣勢踏上戰場，激情迸發不留遺憾。'}
        </p>
      </div>

      {/* Motto */}
      <div className="px-4 pb-4 pt-3">
        <div className="flex items-center gap-2 mb-2">
          <i className="ri-chat-quote-line text-xs" style={{ color: `${accentColor}80` }} />
          <span className="text-[10px] tracking-widest uppercase text-[#555]">戰隊口號</span>
          {!editing && (
            canEdit ? (
              <button onClick={() => { setDraft(motto); setEditing(true); }}
                className="ml-auto flex items-center gap-1.5 text-xs text-[#555] hover:text-[#aaa] cursor-pointer transition-colors py-1 px-2 rounded-md hover:bg-[#1a1a1a]"
              >
                <i className="ri-pencil-line" /> 編輯
              </button>
            ) : (
              <span className="ml-auto flex items-center gap-1 text-[10px] text-[#333]">
                <i className="ri-lock-line" /> 僅本隊可編輯
              </span>
            )
          )}
        </div>
        {editing ? (
          <div className="space-y-2">
            <input type="text" value={draft} onChange={e => setDraft(e.target.value)}
              maxLength={40} placeholder="輸入你們的戰隊口號…"
              className="w-full bg-[#1a1a1a] border rounded-xl px-4 py-3 text-white text-sm placeholder-[#444] outline-none"
              style={{ borderColor: `${accentColor}40` }} autoFocus
            />
            <div className="flex gap-2">
              <button onClick={handleSave}
                className="flex-1 py-3 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap"
                style={{ background: accentColor, color: '#0d0d0d', boxShadow: `0 0 14px ${accentColor}55` }}
              >
                儲存
              </button>
              <button onClick={handleCancel}
                className="flex-1 py-3 rounded-xl text-sm border border-[#2a2a2a] text-[#666] hover:text-[#aaa] cursor-pointer transition-all whitespace-nowrap"
              >
                取消
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm font-bold tracking-wider italic min-h-[1.25rem]"
            style={{ color: motto ? accentColor : '#444', textShadow: motto ? `0 0 14px ${accentColor}65` : 'none' }}
          >
            {motto ? (
              <>
                {typedMotto}
                {typedMotto.length < motto.length && (
                  <span className="inline-block w-0.5 h-3.5 ml-0.5 align-middle animate-pulse" style={{ background: accentColor }} />
                )}
              </>
            ) : '尚未設定口號'}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Floating particles ────────────────────────────────────────────────────────
function FloatingParticles() {
  const particles = useMemo(() => Array.from({ length: 28 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 3 + 0.8,
    duration: Math.random() * 8 + 6,
    delay: Math.random() * 8,
    opacity: Math.random() * 0.4 + 0.1,
    color: i % 5 === 0 ? '#ffd700' : i % 4 === 0 ? '#d4a843' : i % 3 === 0 ? '#7ec8a0' : i % 2 === 0 ? '#a78bfa' : '#e0e0e0',
  })), []);
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {particles.map(p => (
        <div key={p.id} className="absolute rounded-full"
          style={{
            left: `${p.x}%`, top: `${p.y}%`,
            width: p.size, height: p.size,
            background: p.color, opacity: p.opacity,
            boxShadow: `0 0 ${p.size * 3}px ${p.color}`,
            animation: `rosterFloat ${p.duration}s ${p.delay}s ease-in-out infinite`,
          }}
        />
      ))}
    </div>
  );
}

// ── VS Divider ────────────────────────────────────────────────────────────────
function VSDivider() {
  return (
    <div className="hidden md:flex flex-col items-center justify-center py-4 select-none">
      <div className="flex-1 w-px" style={{ background: 'linear-gradient(180deg, transparent, rgba(212,168,67,0.2), transparent)', minHeight: '40px' }} />
      <div className="relative my-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center font-black text-xs"
          style={{
            background: 'linear-gradient(135deg, #d4a84320, #0d0d0d)',
            border: '1px solid #d4a84345',
            color: '#d4a843',
            boxShadow: '0 0 20px rgba(212,168,67,0.25), 0 0 40px rgba(212,168,67,0.1)',
            animation: 'vsPulse 3s ease-in-out infinite',
          }}
        >
          VS
        </div>
        {/* orbiting dot */}
        <div className="absolute inset-0 rounded-full" style={{ animation: 'orbitDot 4s linear infinite' }}>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-[#d4a843]"
            style={{ boxShadow: '0 0 6px rgba(212,168,67,0.9)' }}
          />
        </div>
      </div>
      <div className="flex-1 w-px" style={{ background: 'linear-gradient(180deg, transparent, rgba(224,224,224,0.15), transparent)', minHeight: '40px' }} />
    </div>
  );
}
// ── Main section ──────────────────────────────────────────────────────────────
export default function MemberRosterSection() {
  const { members, myMemberId, setShowMemberModal, selectedTeam, setPendingTeam, scores } = useScore();
  const [viewingMember, setViewingMember] = useState<Member | null>(null);

  const getMemberScore = useCallback((memberId: string) => {
    // 優先用 memberScores（DB 持久化，不受 history 60 筆限制）
    // fallback 到 history 計算（向下相容舊資料）
    if (scores.memberScores && scores.memberScores[memberId] !== undefined) {
      return scores.memberScores[memberId];
    }
    return scores.history
      .filter(e => e.memberId === memberId)
      .reduce((s, e) => s + e.points, 0);
  }, [scores.history, scores.memberScores]);

  const blackMembers = useMemo(() =>
    members.filter(m => m.team === 'black').slice().sort((a, b) => getMemberScore(b.id) - getMemberScore(a.id)),
    [members, getMemberScore],
  );
  const whiteMembers = useMemo(() =>
    members.filter(m => m.team === 'white').slice().sort((a, b) => getMemberScore(b.id) - getMemberScore(a.id)),
    [members, getMemberScore],
  );

  const blackCount = useAnimatedCount(blackMembers.length);
  const totalCount = useAnimatedCount(members.length);
  const whiteCount = useAnimatedCount(whiteMembers.length);

  const handleJoin = () => {
    if (selectedTeam) setPendingTeam(selectedTeam);
    setShowMemberModal(true);
  };
  void handleJoin;

  return (
    <section id="members" className="relative overflow-hidden py-14 sm:py-20" style={{ background: '#06060f' }}>

      {/* ── Decorative background ── */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, rgba(212,168,67,0.035) 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        {/* Aurora orbs */}
        <div className="absolute top-0 left-1/4 w-[500px] h-80 blur-[100px]" style={{ background: 'radial-gradient(ellipse, rgba(167,139,250,0.18), transparent 70%)', animation: 'auroraOrb1 12s ease-in-out infinite', opacity: 0.7 }} />
        <div className="absolute bottom-0 right-1/4 w-96 h-72 blur-[90px]" style={{ background: 'radial-gradient(ellipse, rgba(212,168,67,0.22), transparent 70%)', animation: 'auroraOrb2 15s ease-in-out infinite', opacity: 0.6 }} />
        <div className="absolute top-1/3 left-0 w-72 h-96 blur-[80px]" style={{ background: 'radial-gradient(ellipse, rgba(167,139,250,0.12), transparent 70%)', animation: 'auroraOrb3 18s ease-in-out infinite', opacity: 0.5 }} />
        <div className="absolute top-1/4 right-0 w-72 h-72 blur-[80px]" style={{ background: 'radial-gradient(ellipse, rgba(126,200,160,0.15), transparent 70%)', animation: 'auroraOrb4 14s ease-in-out infinite', opacity: 0.6 }} />
        {/* Big center glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-52 blur-[80px] opacity-20"
          style={{ background: 'radial-gradient(ellipse, rgba(212,168,67,0.7) 0%, transparent 70%)' }}
        />
        {/* Edge lines */}
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,168,67,0.5), transparent)' }} />
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,168,67,0.15), transparent)' }} />
        {/* Corner decor */}
        <div className="absolute top-6 left-6 w-8 h-8 border-l-2 border-t-2 border-[#d4a843]/35" />
        <div className="absolute top-6 right-6 w-8 h-8 border-r-2 border-t-2 border-[#d4a843]/35" />
        <div className="absolute bottom-6 left-6 w-8 h-8 border-l border-b border-[#d4a843]/18" />
        <div className="absolute bottom-6 right-6 w-8 h-8 border-r border-b border-[#d4a843]/18" />
      </div>

      <FloatingParticles />

      <div className="max-w-5xl mx-auto px-5 relative z-10">

        {/* ── Epic Header ── */}
        <div className="text-center mb-10 sm:mb-14">
          {/* Top label */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-12 sm:w-24 relative overflow-hidden">
              <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, #d4a843)', width: '100%' }} />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, transparent, #d4a84370, transparent)', animation: 'scanLine 2.5s linear infinite' }} />
            </div>
            <div className="flex items-center gap-2 px-4 py-1.5 rounded-full relative overflow-hidden"
              style={{ background: '#d4a84310', border: '1px solid #d4a84440', boxShadow: '0 0 24px rgba(212,168,67,0.2)' }}
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#d4a843] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#d4a843]" />
              </span>
              <p className="text-[#d4a843] text-[10px] tracking-[0.5em] uppercase font-black font-cinzel">Battle Roster</p>
            </div>
            <div className="h-px w-12 sm:w-24" style={{ background: 'linear-gradient(90deg, #d4a843, transparent)' }} />
          </div>

          {/* Main title */}
          <div className="relative inline-block mb-4">
            <h2 className="text-white text-3xl sm:text-5xl font-black font-serif leading-tight"
              style={{ textShadow: '0 0 40px rgba(212,168,67,0.6), 0 0 80px rgba(212,168,67,0.2), 0 2px 0 rgba(0,0,0,0.5)' }}
            >
              出征名單
            </h2>
            <div className="absolute -bottom-2 left-0 right-0 h-px"
              style={{ background: 'linear-gradient(90deg, transparent, #d4a843, transparent)' }}
            />
          </div>

          <div className="flex items-center justify-center gap-3 mb-4">
            {[1,2,3].map(i => (
              <div key={i} className={`rounded-full ${i === 2 ? 'w-2 h-2' : 'w-1.5 h-1.5'} bg-[#d4a843]`}
                style={{
                  boxShadow: `0 0 8px #d4a843, 0 0 16px rgba(212,168,67,0.5)`,
                  animation: `gentlePulse ${1.8 + i * 0.3}s ease-in-out infinite`,
                  animationDelay: `${i * 0.2}s`,
                }}
              />
            ))}
          </div>
          <p className="text-[#555] text-xs sm:text-sm max-w-sm mx-auto">
            每位踏上這場冒險的靈魂，都在這裡留下印記
          </p>
        </div>

        {/* ── Stats ── */}
        <div className="flex items-center justify-center gap-4 sm:gap-8 mb-10 sm:mb-10">
          {/* Black count */}
          <div className="text-center">
            <div key={blackCount.popKey}
              className={`text-3xl sm:text-5xl font-black tabular-nums font-serif ${blackCount.popKey > 0 ? 'member-pop' : ''}`}
              style={{ color: '#d4a843', textShadow: '0 0 20px rgba(212,168,67,0.7), 0 0 40px rgba(212,168,67,0.3)' }}
            >
              {blackCount.display}
            </div>
            <div className="text-[#555] text-[10px] tracking-[0.4em] uppercase mt-1 font-cinzel">黑隊</div>
          </div>

          <div className="w-px h-12 sm:h-16" style={{ background: 'linear-gradient(180deg, transparent, #d4a84335, transparent)' }} />

          {/* Total */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full border-2 border-[#d4a843] mb-1 relative"
              style={{ boxShadow: '0 0 25px rgba(212,168,67,0.4), 0 0 50px rgba(212,168,67,0.15), inset 0 0 15px rgba(212,168,67,0.08)', animation: 'ringPulse 3s ease-in-out infinite' }}
            >
              <div className="absolute inset-0 rounded-full" style={{ background: 'radial-gradient(circle, rgba(212,168,67,0.1), transparent)' }} />
              <div key={totalCount.popKey + 100}
                className={`text-white text-3xl sm:text-4xl font-black tabular-nums font-serif relative z-10 ${totalCount.popKey > 0 ? 'member-pop' : ''}`}
              >
                {totalCount.display}
              </div>
            </div>
            <div className="text-[#555] text-[10px] tracking-[0.4em] uppercase font-cinzel">總人數</div>
          </div>

          <div className="w-px h-12 sm:h-16" style={{ background: 'linear-gradient(180deg, transparent, #e0e0e020, transparent)' }} />

          {/* White count */}
          <div className="text-center">
            <div key={whiteCount.popKey + 200}
              className={`text-3xl sm:text-5xl font-black tabular-nums font-serif ${whiteCount.popKey > 0 ? 'member-pop' : ''}`}
              style={{ color: '#e0e0e0', textShadow: '0 0 20px rgba(220,220,220,0.5), 0 0 40px rgba(220,220,220,0.2)' }}
            >
              {whiteCount.display}
            </div>
            <div className="text-[#555] text-[10px] tracking-[0.4em] uppercase mt-1 font-cinzel">白隊</div>
          </div>
        </div>

        {/* Score legend */}
        <div className="flex items-center justify-center gap-4 mb-6 sm:mb-8 flex-wrap">
          {[
            { rank: 1, bg: 'linear-gradient(135deg, #ffd700, #d4a843)', content: <i className="ri-crown-fill text-[8px] text-[#0d0d0d]" />, label: '積分第一', shadow: '0 0 8px rgba(212,168,67,0.7)' },
            { rank: 2, bg: 'linear-gradient(135deg, #c0c0c0, #888)', content: <span className="text-[7px] font-black text-[#0d0d0d]">2</span>, label: '第二名', shadow: '0 0 6px rgba(200,200,200,0.5)' },
            { rank: 3, bg: 'linear-gradient(135deg, #cd7f32, #a05a20)', content: <span className="text-[7px] font-black text-white">3</span>, label: '第三名', shadow: '0 0 6px rgba(180,100,30,0.4)' },
          ].map(item => (
            <div key={item.rank} className="flex items-center gap-1.5">
              <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: item.bg, boxShadow: item.shadow }}>
                {item.content}
              </div>
              <span className="text-[10px] text-[#555] tracking-wide">{item.label}</span>
            </div>
          ))}
          <div className="w-px h-3" style={{ background: '#2a2a2a' }} />
          <div className="flex items-center gap-1.5">
            <i className="ri-sort-desc text-[#d4a843] text-xs" style={{ filter: 'drop-shadow(0 0 4px rgba(212,168,67,0.7))' }} />
            <span className="text-[10px] text-[#555] tracking-wide">依積分排序</span>
          </div>
        </div>

        {/* ── Rosters ── */}
        <div className="flex flex-col md:flex-row gap-4 sm:gap-0 items-stretch">

          {/* Black Team */}
          <div className="flex-1 rounded-2xl overflow-hidden relative"
            style={{
              background: 'linear-gradient(160deg, #0e0d16 0%, #090a14 100%)',
              border: '1.5px solid #d4a84330',
              boxShadow: '0 0 30px rgba(212,168,67,0.08), inset 0 0 20px rgba(212,168,67,0.03)',
              animation: 'blackTeamNeonPulse 4s ease-in-out infinite',
            }}
          >
            <div className="px-4 sm:px-5 py-3 sm:py-4 border-b flex items-center gap-3 relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #151018, #090a14)', borderColor: '#d4a84325' }}
            >
              <div className="absolute inset-0 opacity-25" style={{ background: 'radial-gradient(ellipse at 20% 50%, #d4a84322, transparent 70%)' }} />
              <div className="absolute top-0 left-0 right-0 h-px overflow-hidden">
                <div style={{ height: '100%', background: 'linear-gradient(90deg, transparent, rgba(212,168,67,0.8), transparent)', animation: 'headerScan 3s linear infinite' }} />
              </div>
              <div className="w-9 h-9 flex items-center justify-center rounded-full relative z-10"
                style={{ background: '#d4a84322', border: '1.5px solid #d4a84355', boxShadow: '0 0 18px rgba(212,168,67,0.4)' }}
              >
                <i className="ri-shield-fill text-sm text-[#d4a843]" style={{ filter: 'drop-shadow(0 0 5px rgba(212,168,67,0.9))' }} />
              </div>
              <div className="relative z-10">
                <span className="text-white text-sm font-black tracking-wide" style={{ textShadow: '0 0 10px rgba(212,168,67,0.35)' }}>黑隊</span>
                <span className="text-[#d4a84375] text-[10px] ml-2 tracking-widest uppercase font-cinzel">Black Team</span>
              </div>
              <div className="ml-auto z-10">
                <span className="text-[10px] font-black px-2.5 py-1 rounded-full"
                  style={{ background: '#d4a84318', color: '#d4a843', border: '1px solid #d4a84338', boxShadow: '0 0 10px rgba(212,168,67,0.2)' }}
                >
                  {blackMembers.length} 人
                </span>
              </div>
            </div>

            <div className="p-3 sm:p-4" style={{ background: '#090a14' }}>
              <TeamIntroCard teamName="black" canEdit={selectedTeam === 'black'} />
              <div className="space-y-2">
                {blackMembers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-24 gap-2">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full" style={{ background: '#d4a84310', border: '1px dashed #d4a84328' }}>
                      <i className="ri-user-add-line text-lg" style={{ color: '#d4a84345' }} />
                    </div>
                    <p className="text-[#333] text-xs">尚無勇士加入黑隊</p>
                  </div>
                ) : (
                  blackMembers.map((m, idx) => (
                    <MemberCard key={m.id} member={m} isMe={m.id === myMemberId}
                      rank={idx + 1} score={getMemberScore(m.id)} onView={() => setViewingMember(m)}
                    />
                  ))
                )}
              </div>
            </div>
          </div>

          {/* VS Divider */}
          <VSDivider />

          {/* White Team */}
          <div className="flex-1 rounded-2xl overflow-hidden relative"
            style={{
              background: 'linear-gradient(160deg, #0c0d18 0%, #090a14 100%)',
              border: '1.5px solid rgba(220,220,220,0.12)',
              boxShadow: '0 0 25px rgba(220,220,220,0.05), inset 0 0 20px rgba(255,255,255,0.02)',
              animation: 'whiteTeamNeonPulse 5s ease-in-out infinite',
            }}
          >
            <div className="px-4 sm:px-5 py-3 sm:py-4 border-b flex items-center gap-3 relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #111220, #090a14)', borderColor: '#ffffff12' }}
            >
              <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(ellipse at 20% 50%, rgba(220,220,220,0.1), transparent 70%)' }} />
              <div className="absolute top-0 left-0 right-0 h-px overflow-hidden">
                <div style={{ height: '100%', background: 'linear-gradient(90deg, transparent, rgba(220,220,220,0.6), transparent)', animation: 'headerScan 4s linear infinite' }} />
              </div>
              <div className="w-9 h-9 flex items-center justify-center rounded-full relative z-10"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1.5px solid rgba(255,255,255,0.25)', boxShadow: '0 0 18px rgba(255,255,255,0.12)' }}
              >
                <i className="ri-sword-fill text-sm text-white" style={{ filter: 'drop-shadow(0 0 5px rgba(255,255,255,0.7))' }} />
              </div>
              <div className="relative z-10">
                <span className="text-white text-sm font-black tracking-wide" style={{ textShadow: '0 0 10px rgba(255,255,255,0.2)' }}>白隊</span>
                <span className="text-[#88888870] text-[10px] ml-2 tracking-widest uppercase font-cinzel">White Team</span>
              </div>
              <div className="ml-auto z-10">
                <span className="text-[10px] font-black px-2.5 py-1 rounded-full"
                  style={{ background: 'rgba(255,255,255,0.07)', color: '#bbb', border: '1px solid rgba(255,255,255,0.14)', boxShadow: '0 0 8px rgba(255,255,255,0.05)' }}
                >
                  {whiteMembers.length} 人
                </span>
              </div>
            </div>

            <div className="p-3 sm:p-4" style={{ background: '#090a14' }}>
              <TeamIntroCard teamName="white" canEdit={selectedTeam === 'white'} />
              <div className="space-y-2">
                {whiteMembers.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-24 gap-2">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full" style={{ background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.1)' }}>
                      <i className="ri-user-add-line text-lg text-[#444]" />
                    </div>
                    <p className="text-[#333] text-xs">尚無勇士加入白隊</p>
                  </div>
                ) : (
                  whiteMembers.map((m, idx) => (
                    <MemberCard key={m.id} member={m} isMe={m.id === myMemberId}
                      rank={idx + 1} score={getMemberScore(m.id)} onView={() => setViewingMember(m)}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

      </div>

      {viewingMember && createPortal(
        <MemberViewModal member={viewingMember} onClose={() => setViewingMember(null)} />,
        document.body
      )}

      <style>{`
        @keyframes rosterFloat {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-20px) scale(1.3); }
        }
        @keyframes auroraOrb1 {
          0%, 100% { transform: translate(0, 0); }
          33% { transform: translate(50px, 35px); }
          66% { transform: translate(-25px, 55px); }
        }
        @keyframes auroraOrb2 {
          0%, 100% { transform: translate(0, 0); }
          33% { transform: translate(-55px, -30px); }
          66% { transform: translate(35px, -45px); }
        }
        @keyframes auroraOrb3 {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(35px, -45px); }
        }
        @keyframes auroraOrb4 {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(-35px, 40px); }
        }
        @keyframes scanLine {
          from { transform: translateX(-100%); }
          to { transform: translateX(100%); }
        }
        @keyframes headerScan {
          from { transform: translateX(-100%); }
          to { transform: translateX(200%); }
        }
        @keyframes gentlePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(1.4); }
        }
        @keyframes blackTeamNeonPulse {
          0%, 100% { box-shadow: 0 0 20px rgba(212,168,67,0.08), 0 0 0 1px rgba(212,168,67,0.12); }
          50% { box-shadow: 0 0 40px rgba(212,168,67,0.18), 0 0 0 1px rgba(212,168,67,0.25); }
        }
        @keyframes whiteTeamNeonPulse {
          0%, 100% { box-shadow: 0 0 20px rgba(220,220,220,0.04); }
          50% { box-shadow: 0 0 40px rgba(220,220,220,0.1); }
        }
        @keyframes member-pop {
          0% { transform: scale(1); }
          40% { transform: scale(1.18); }
          100% { transform: scale(1); }
        }
        @keyframes vsPulse {
          0%, 100% { box-shadow: 0 0 20px rgba(212,168,67,0.25), 0 0 40px rgba(212,168,67,0.1); }
          50% { box-shadow: 0 0 30px rgba(212,168,67,0.45), 0 0 60px rgba(212,168,67,0.2); }
        }
        @keyframes orbitDot {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes ringPulse {
          0%, 100% { box-shadow: 0 0 25px rgba(212,168,67,0.4), 0 0 50px rgba(212,168,67,0.15); }
          50% { box-shadow: 0 0 35px rgba(212,168,67,0.6), 0 0 70px rgba(212,168,67,0.25); }
        }
        @keyframes cardShimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .member-pop { animation: member-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
      `}</style>
    </section>
  );
}
